import { useApp } from "../context/AppContext";
import {
  MapPin, Heart, AlertTriangle, CheckCircle2, ExternalLink,
  Ticket, TicketX, Baby, Home as HomeIcon, Trees, Clock, Share2,
} from "lucide-react";
import { formatEuro } from "../lib/format";

function bookingLink(item, planLocation) {
  if (item.booking_url) return item.booking_url;
  const q = `${item.venue_name || item.description} ${planLocation || ""} web oficial`.trim().replace(/\s+/g, " ");
  return `https://duckduckgo.com/?q=%21ducky+${encodeURIComponent(q)}`;
}

function planLink(plan) {
  if (plan.booking_url) return plan.booking_url;
  const q = `${plan.title} ${plan.location} web oficial reservar`.trim().replace(/\s+/g, " ");
  return `https://duckduckgo.com/?q=%21ducky+${encodeURIComponent(q)}`;
}

function buildWhatsAppText(plan, lang = "es") {
  const lines = [];
  lines.push(`*${plan.title}*`);
  lines.push(`📍 ${plan.location} · ${plan.distance_info}`);
  lines.push(`💰 ~${formatEuro(plan.price_per_person)}/${lang === "es" ? "persona" : "person"} — ${lang === "es" ? "total" : "total"}: ~${formatEuro(plan.total_group)}`);
  if (plan.duration_hours) lines.push(`⏱️ ~${plan.duration_hours}h`);
  lines.push("");
  for (const it of plan.itinerary || []) {
    let line = `• ${it.time} — ${it.description}`;
    if (it.venue_name) line += ` (${it.venue_name})`;
    if (it.cost_per_person != null) line += ` ~${formatEuro(it.cost_per_person)}/p`;
    lines.push(line);
  }
  lines.push("");
  lines.push(lang === "es" ? "Generado con Planificador de Planes ✨" : "Made with Plan Planner ✨");
  return lines.join("\n");
}

export default function PlanCard({ plan, index, onSave, saved, canSave, onRating, rating, onToggleDone, done, showFavActions = false }) {
  const { t, lang } = useApp();
  const over = plan.over_budget_by && plan.over_budget_by > 0;

  const handleWhatsApp = () => {
    const text = buildWhatsAppText(plan, lang);
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      data-testid={`plan-card-${index}`}
      className={`group bg-white rounded-3xl border p-6 sm:p-7 shadow-[0_2px_16px_rgba(28,25,23,0.05)] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${
        done ? "opacity-70 border-[#2C5E3B]/40" : "border-[#E7E0D5]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider font-mono text-[#D95338] font-semibold">
              #{String(index + 1).padStart(2, "0")}
            </span>
            {plan.tags?.slice(0, 2).map((tag) => (
              <span key={tag} className="text-[10px] uppercase tracking-wider bg-[#F2EDE4] text-stone-600 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
            {plan.generated_by && (
              <span className="text-[10px] uppercase tracking-wider bg-stone-900 text-white px-2 py-0.5 rounded-full">
                {plan.generated_by === "gpt" ? "GPT" : "Claude"}
              </span>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-stone-900 leading-tight">{plan.title}</h3>
          <p className="text-sm text-stone-600 mt-1.5 flex items-start gap-1.5">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#2C5E3B]" />
            <span>{plan.location} · {plan.distance_info}</span>
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">~{formatEuro(plan.price_per_person)}</div>
          <div className="text-xs text-stone-500 font-mono uppercase tracking-wider">{t.perPerson}</div>
        </div>
      </div>

      {/* Meta badges row */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-stone-500 font-mono">{t.totalGroup}:</span>
        <span className="text-sm font-semibold text-stone-800">~{formatEuro(plan.total_group)}</span>
        {over ? (
          <Badge testid={`over-budget-${index}`} tone="warning" icon={<AlertTriangle className="w-3.5 h-3.5" />}>
            {t.overBudget(formatEuro(plan.over_budget_by).replace(" €", ""))}
          </Badge>
        ) : (
          <Badge tone="success" icon={<CheckCircle2 className="w-3.5 h-3.5" />}>{t.within}</Badge>
        )}
        {plan.requires_booking ? (
          <Badge tone="warning" icon={<Ticket className="w-3.5 h-3.5" />}>{t.requiresBooking}</Badge>
        ) : (
          <Badge tone="neutral" icon={<TicketX className="w-3.5 h-3.5" />}>{t.noBooking}</Badge>
        )}
        {plan.suitable_indoor && (
          <Badge tone="neutral" icon={<HomeIcon className="w-3.5 h-3.5" />}>{t.indoor}</Badge>
        )}
        {plan.suitable_outdoor && !plan.suitable_indoor && (
          <Badge tone="neutral" icon={<Trees className="w-3.5 h-3.5" />}>{t.outdoor}</Badge>
        )}
        {plan.suitable_for_kids && (
          <Badge tone="neutral" icon={<Baby className="w-3.5 h-3.5" />}>{t.kidFriendly}</Badge>
        )}
        {plan.duration_hours && (
          <Badge tone="neutral" icon={<Clock className="w-3.5 h-3.5" />}>{t.duration_h(plan.duration_hours)}</Badge>
        )}
      </div>

      <ol className="mt-5 relative border-l-2 border-dashed border-[#E7E0D5] pl-5 space-y-4">
        {plan.itinerary?.map((it, i) => {
          const href = bookingLink(it, plan.location);
          return (
            <li key={i} className="relative">
              <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-[#D95338] ring-4 ring-[#FAF7F2]" />
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs font-mono font-semibold text-[#2C5E3B]">{it.time}</span>
                <span className="text-sm text-stone-800 leading-snug">{it.description}</span>
                {it.cost_per_person != null && (
                  <span className="text-xs text-stone-500 whitespace-nowrap">— ~{formatEuro(it.cost_per_person)}/pers</span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                {it.venue_name && <span className="text-xs text-stone-500">📍 {it.venue_name}</span>}
                <a href={href} target="_blank" rel="noopener noreferrer"
                  data-testid={`link-book-${index}-${i}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#D95338] hover:text-[#C24127] hover:underline">
                  {it.booking_url ? t.bookNow : t.findOnline}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              {it.note && <p className="text-xs text-stone-500 mt-0.5">{it.note}</p>}
            </li>
          );
        })}
      </ol>

      <div className="mt-6 pt-4 border-t border-[#E7E0D5]/70 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <a href={planLink(plan)} target="_blank" rel="noopener noreferrer"
            data-testid={`link-plan-open-${index}`}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#2C5E3B] text-white hover:bg-[#21472C] active:scale-95 transition-all">
            <ExternalLink className="w-4 h-4" />
            {plan.booking_url ? t.bookPlan : t.exploreOnline}
          </a>
          <button type="button" onClick={handleWhatsApp} data-testid={`button-whatsapp-${index}`}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#25D366] text-white hover:bg-[#1FB855] active:scale-95 transition-all">
            <Share2 className="w-4 h-4" />
            {t.shareWhatsapp}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showFavActions && (
            <>
              <button data-testid={`rate-up-${index}`} onClick={() => onRating?.("up")}
                className={`p-2 rounded-full border transition-all ${rating === "up" ? "bg-[#EDF7EF] border-[#2C5E3B] text-[#2C5E3B]" : "bg-white border-[#E7E0D5] text-stone-500 hover:border-[#2C5E3B]"}`}
                title={t.ratingUp}>
                👍
              </button>
              <button data-testid={`rate-down-${index}`} onClick={() => onRating?.("down")}
                className={`p-2 rounded-full border transition-all ${rating === "down" ? "bg-[#FFF4EC] border-[#B54708] text-[#B54708]" : "bg-white border-[#E7E0D5] text-stone-500 hover:border-[#B54708]"}`}
                title={t.ratingDown}>
                👎
              </button>
              <button data-testid={`toggle-done-${index}`} onClick={() => onToggleDone?.(!done)}
                className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-all ${done ? "bg-[#2C5E3B] text-white border-[#2C5E3B]" : "bg-white border-[#E7E0D5] text-stone-700 hover:border-[#2C5E3B]"}`}>
                {done ? t.marked : t.markDone}
              </button>
            </>
          )}
          {canSave && !showFavActions ? (
            <button data-testid={`button-save-${index}`} onClick={() => onSave(plan)} disabled={saved}
              className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-all active:scale-95 ${
                saved ? "bg-[#EDF7EF] text-[#2C5E3B] cursor-default" : "bg-stone-900 text-white hover:bg-stone-700"
              }`}>
              <Heart className={`w-4 h-4 ${saved ? "fill-[#2C5E3B]" : ""}`} />
              {saved ? t.saved : t.save}
            </button>
          ) : null}
          {!canSave && !showFavActions && <p className="text-xs text-stone-500">{t.loginToSave}</p>}
        </div>
      </div>
    </article>
  );
}

function Badge({ tone = "neutral", icon, children, testid }) {
  const tones = {
    success: "bg-[#EDF7EF] text-[#2C5E3B] border-[#C9E5CE]",
    warning: "bg-[#FFF4EC] text-[#B54708] border-[#FDCC9D]",
    neutral: "bg-[#F2EDE4] text-stone-700 border-[#E7E0D5]",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${tones[tone]}`}>
      {icon}{children}
    </span>
  );
}
